import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MasterFilter, {
  type DateRangeValue,
  type FilterFieldConfig,
  useMasterFilter,
} from 'components/common/MasterFilter';
import { useBusinessUnits } from 'contexts/BusinessUnitsContext';
import { useSelectedBusinessUnit } from 'contexts/SidebarContext';
import moment from 'moment';
import type React from 'react';
import { useMemo } from 'react';
import { useGetSellerFilterOptionsQuery } from 'services/api/user.api';
import { MOBILE_PAGE_PT, NAVY } from 'styles/tokens';
import { INSUREX_BUSINESS_UNIT_ID } from 'utils/constants';

import SellerDashboard from '../Dashboard/components/SellerDashboard';

const SellerManagementPage: React.FC = () => {
  const { selectedBusinessUnitId: businessUnitId } = useSelectedBusinessUnit();
  const { getBusinessUnitById } = useBusinessUnits();

  const hideUrgent = businessUnitId === INSUREX_BUSINESS_UNIT_ID;
  const isCarDealership =
    businessUnitId !== null && businessUnitId !== -1
      ? getBusinessUnitById(businessUnitId)?.type === 'car_dealership'
      : false;

  const { data: sellerFilterOptions } = useGetSellerFilterOptionsQuery(
    { businessUnitId: businessUnitId ?? 0 },
    { skip: !isCarDealership || businessUnitId === null || businessUnitId === -1 }
  );

  const filterConfig = useMemo<FilterFieldConfig[]>(() => {
    const base: FilterFieldConfig[] = [
      {
        key: 'dateRange',
        kind: 'dateRange',
        label: 'Fecha creación',
        placement: 'bar',
        defaultValue: {
          start: moment().subtract(7, 'days').startOf('day'),
          end: moment().endOf('day'),
        } as DateRangeValue,
      },
      {
        key: 'searchTerm',
        kind: 'text',
        label: 'Buscar',
        placement: 'bar',
        placeholder: 'ID, Nombre, teléfono, email o RUT',
      },
      {
        key: 'status',
        kind: 'singleSelect',
        label: 'Estado',
        placement: 'bar',
        options: [
          { value: 'open', label: 'Solo abiertas' },
          { value: 'por-responder', label: 'Por responder' },
          ...(hideUrgent ? [] : [{ value: 'urgentes', label: 'Urgentes' }]),
        ],
      },
    ];

    if (isCarDealership) {
      base.push(
        {
          key: 'brands',
          kind: 'multiSelect',
          label: 'Marca',
          placement: 'bar',
          options: (sellerFilterOptions?.brands ?? []).map((b) => ({ value: b, label: b })),
        },
        {
          key: 'branchOffices',
          kind: 'multiSelect',
          label: 'Sucursal',
          placement: 'bar',
          options: (sellerFilterOptions?.branchOffices ?? []).map((b) => ({ value: b, label: b })),
        }
      );
    }

    return base;
  }, [hideUrgent, isCarDealership, sellerFilterOptions]);

  const filter = useMasterFilter(filterConfig, businessUnitId);
  const dateRange = filter.values.dateRange as DateRangeValue | undefined;
  const startDate = dateRange?.start ?? null;
  const endDate = dateRange?.end ?? null;
  const searchTerm = (filter.values.searchTerm as string) ?? '';
  const statusFilter = (filter.values.status as string) ?? '';
  const brandsFilter = (filter.values.brands as string[] | undefined) ?? [];
  const branchOfficesFilter = (filter.values.branchOffices as string[] | undefined) ?? [];

  const hasBusinessUnit = businessUnitId !== null && businessUnitId !== -1;

  return (
    <Box sx={{ px: 2, pt: { xs: `${MOBILE_PAGE_PT}px`, md: 2 } }}>
      {hasBusinessUnit ? (
        <>
          <Box sx={{ pb: 2 }}>
            <MasterFilter {...filter} />
          </Box>
          {startDate && endDate && (
            <SellerDashboard
              businessUnitId={businessUnitId}
              startDate={startDate.toISOString()}
              endDate={endDate.toISOString()}
              clientIdFilter={searchTerm}
              statusFilter={statusFilter}
              brands={brandsFilter}
              branchOffices={branchOfficesFilter}
            />
          )}
        </>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 'calc(100vh - 120px)',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: '1.125rem',
              color: NAVY,
              mb: 1,
            }}
          >
            Selecciona una unidad de negocio
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontSize: '0.95rem',
              color: `${NAVY}99`,
            }}
          >
            Escoge una unidad de negocio en la barra lateral para visualizar las métricas.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SellerManagementPage;
